import { useApi } from "../hooks/useApi";
import { theme, fonts } from "../theme";
import { Card, CardHeader, Metric, Pill, ErrorState, formatPnl, pnlColor } from "../components/ui";
import { BarChart } from "../components/EquityChart";
import { SkeletonCards } from "../components/shared";

export default function RiskDashboard() {
  const { data: trades, error, loading } = useApi("/trades?limit=500", 5000);
  const { data: summaries } = useApi("/daily-summaries?days=30", 30000);
  const { data: status } = useApi("/status", 5000);

  if (loading) return <SkeletonCards n={4} />;
  if (error) return <ErrorState error={error} />;

  const exits = (trades?.trades || []).filter((t) => t.action === "exit");
  const dailyData = (summaries?.summaries || []).map((d) => d.pnl || 0);
  const dailyLabels = (summaries?.summaries || []).map((d, i) => i % 5 === 0 ? `D${i + 1}` : "");
  const strategies = status?.strategies || {};

  // Max DD calculation
  let maxDD = 0, peak = 0, cumPnl = 0;
  exits.forEach((t) => { cumPnl += t.pnl || 0; if (cumPnl > peak) peak = cumPnl; const dd = cumPnl - peak; if (dd < maxDD) maxDD = dd; });

  // Daily VaR (simplified: 95th percentile)
  const sortedDaily = [...dailyData].sort((a, b) => a - b);
  const varIdx = Math.floor(sortedDaily.length * 0.05);
  const var95 = sortedDaily[varIdx] || 0;

  const avgDaily = dailyData.length > 0 ? dailyData.reduce((a, b) => a + b, 0) / dailyData.length : 0;
  const avgWin = exits.filter((t) => (t.pnl || 0) > 0);
  const avgLoss = exits.filter((t) => (t.pnl || 0) < 0);
  const avgWinVal = avgWin.length > 0 ? avgWin.reduce((s, t) => s + t.pnl, 0) / avgWin.length : 0;
  const avgLossVal = avgLoss.length > 0 ? Math.abs(avgLoss.reduce((s, t) => s + t.pnl, 0) / avgLoss.length) : 1;
  const rrRatio = avgLossVal > 0 ? (avgWinVal / avgLossVal).toFixed(2) : "—";

  // Correlation from actual trade data (daily P&L per strategy)
  const stratNames = Object.keys(strategies);
  const pairs = [];
  if (stratNames.length >= 2) {
    // Build daily P&L series per strategy
    const dailyPnl = {};
    exits.forEach(t => {
      if (!t.timestamp || !t.strategy) return;
      const day = t.timestamp.slice(0, 10);
      if (!dailyPnl[t.strategy]) dailyPnl[t.strategy] = {};
      dailyPnl[t.strategy][day] = (dailyPnl[t.strategy][day] || 0) + (t.pnl || 0);
    });
    // Pearson correlation between pairs
    for (let i = 0; i < stratNames.length; i++) {
      for (let j = i + 1; j < stratNames.length; j++) {
        const a = stratNames[i], b = stratNames[j];
        const daysA = dailyPnl[a] || {}, daysB = dailyPnl[b] || {};
        const commonDays = Object.keys(daysA).filter(d => d in daysB);
        if (commonDays.length < 5) { pairs.push({ a, b, v: 0, n: commonDays.length }); continue; }
        const va = commonDays.map(d => daysA[d]), vb = commonDays.map(d => daysB[d]);
        const ma = va.reduce((s, x) => s + x, 0) / va.length, mb = vb.reduce((s, x) => s + x, 0) / vb.length;
        let num = 0, da = 0, db = 0;
        for (let k = 0; k < va.length; k++) { num += (va[k] - ma) * (vb[k] - mb); da += (va[k] - ma) ** 2; db += (vb[k] - mb) ** 2; }
        const corr = da > 0 && db > 0 ? num / Math.sqrt(da * db) : 0;
        pairs.push({ a, b, v: corr, n: commonDays.length });
      }
    }
  }

  return (
    <>
      {/* Key Risk Metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { l: "Max Drawdown", v: formatPnl(maxDD), c: theme.redText, s: "all-time" },
          { l: "Daily VaR (95%)", v: formatPnl(var95), c: theme.amberText, s: "estimated" },
          { l: "Avg Daily P&L", v: formatPnl(avgDaily), c: pnlColor(avgDaily), s: "last 30 days" },
          { l: "Risk:Reward", v: `1:${rrRatio}`, c: theme.purpleText, s: "portfolio avg" },
        ].map((m, i) => (
          <Card key={i} style={{ padding: "20px 24px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "1.2px", marginBottom: 8 }}>{m.l}</div>
            <div style={{ fontSize: 28, fontWeight: 900, fontFamily: fonts.mono, color: m.c, lineHeight: 1 }}>{m.v}</div>
            <div style={{ fontSize: 11, color: theme.text3, marginTop: 6 }}>{m.s}</div>
          </Card>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        {/* Daily P&L Distribution */}
        <Card>
          <CardHeader title="Daily P&L" sub="Last 30 trading days" />
          <div style={{ padding: "12px 20px 16px" }}>
            {dailyData.length > 0 ? <BarChart data={dailyData} labels={dailyLabels} height={100} /> : <div style={{ padding: 20, textAlign: "center", color: theme.text3 }}>No daily data yet</div>}
          </div>
        </Card>

        {/* Correlation */}
        <Card>
          <CardHeader title="Correlation" sub="Instrument pairs" />
          <div style={{ padding: "20px 24px", display: "grid", gridTemplateColumns: `repeat(${Math.max(pairs.length, 1)}, 1fr)`, gap: 12 }}>
            {pairs.length > 0 ? pairs.map((c, i) => (
              <div key={i} style={{ padding: 16, borderRadius: 12, background: Math.abs(c.v) > 0.5 ? theme.amberLight : theme.greenLight, textAlign: "center" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: theme.text3, marginBottom: 6 }}>{c.a} {"\u2194"} {c.b}</div>
                <div style={{ fontSize: 24, fontWeight: 900, fontFamily: fonts.mono, color: Math.abs(c.v) > 0.5 ? theme.amberText : theme.greenText }}>{c.v.toFixed(2)}</div>
                <div style={{ fontSize: 10, color: theme.text4, marginTop: 4 }}>{Math.abs(c.v) > 0.5 ? "\u26A0 High" : "\u2713 Low"} {"\u00B7"} {c.n || 0} days</div>
              </div>
            )) : <div style={{ padding: 20, textAlign: "center", color: theme.text3 }}>Need 2+ instruments for correlation</div>}
          </div>
        </Card>
      </div>

      {/* Position Sizing Rules */}
      <Card>
        <CardHeader title="Position Sizing Rules" sub="Risk management configuration" />
        <div style={{ padding: "16px 24px", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          {[
            { h: "Per Trade", items: [["Max Risk", "1.0%"], ["Scaling", "ADX-based"], ["Stop Loss", "Structure"]] },
            { h: "Per Day", items: [["Max Daily Loss", "3.0%"], ["Max Trades", "15/instrument"], ["Cool-off", "30min after 3L"]] },
            { h: "Per Portfolio", items: [["Max Instruments", "3"], ["Correlation", "< 0.5"], ["Margin Buffer", "2x"]] },
          ].map((s, i) => (
            <div key={i} style={{ padding: 16, borderRadius: 12, background: theme.tint }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, color: theme.blueText }}>{s.h}</div>
              {s.items.map(([k, v], j) => (
                <div key={j} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: j < 2 ? `1px solid ${theme.divider}` : "none" }}>
                  <span style={{ fontSize: 11, color: theme.text3 }}>{k}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, fontFamily: fonts.mono }}>{v}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}
